import { ActivatedRoute } from "@angular/router";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { Mesh, BufferGeometry, Material, Color, ColorRepresentation, Vector3 } from "three";
import { GenerateNewShapes } from "../shared/generate-new-shapes";
import { AlteredShapeConfiguration, Object3DUserData, selectedShapeColor } from "@utils/shape";
import { ComponentInteractionService } from "../services/component-interaction/component-interaction.service";
import { MatSnackBar } from "@angular/material/snack-bar";
import { HelperService } from "../shared/helpers/helper.service";
import { Vector2 } from "three";
import { DynamicGenerateShape } from "../shared/face-identifier/face-identifier.model";
import { MatSnackBarMessages } from "../shared/mat-snackbar/mat-snackbar.model";
import { FaceIdentifierService } from "../shared/face-identifier/face-identifier.service";
import { tiltedShapeConfiguration } from "@utils/shape-facetype";
import { Subscription } from "rxjs";
import { CanvasConfig } from "../../../../utils/canvas-configuration";
import { InteractionService } from "../shared/helpers/interaction.service";
import { AuxiliaryObjectType, MeshType } from "~/src/utils/shape-type";
import { SceneManagerService } from "../services/scene-manager/scene-manager.service";
import { SnackBarConfig } from "../constants/snackbar.constants";
import { environment } from "~/src/environments/environment";
import { TranslateService } from "@ngx-translate/core";
import { Object3D, Intersection } from "three";
import { StabilityService } from "../services/stability/stability.service";

@Component({
    selector: "generate-shape",
    templateUrl: "./generate-shape.component.html",
    styleUrls: ["./generate-shape.component.scss"],
    standalone: false,
})
export class GenerateShapeComponent implements OnInit, OnDestroy {
    private intersects: Intersection<Object3D>[] = [];
    private selectedShape: MeshType | null = null;
    private coOrdinates: Vector3 = new Vector3();
    private locationX = 0;
    private locationY = 0;
    private locationZ = 0;
    private nearestGridCords: Vector2 = new Vector2();
    private invalidJoin = false;
    private invalidPointGeneration = false;
    private generatedShape: Mesh<BufferGeometry, Material | Material[]> = new Mesh();
    public selectedColor: ColorRepresentation | undefined;
    public isTilted!: boolean;
    private interceptInfoSub!: Subscription;
    private selectedShapeSub!: Subscription;
    private selectedColorSub!: Subscription;
    private isShapeUnderCopy = false;
    private isFromProductDetail: boolean = false;
    constructor(
        private generateNewShapes: GenerateNewShapes,
        private componetInteractionService: ComponentInteractionService,
        private snackBar: MatSnackBar,
        private helperService: HelperService,
        private faceIdentifierSrv: FaceIdentifierService,
        private interactionService: InteractionService,
        private sceneManagerService: SceneManagerService,
        private route: ActivatedRoute,
        private translate: TranslateService,
        private stabilityService: StabilityService,
    ) {}

    ngOnInit(): void {
        this.interceptInfoSub = this.componetInteractionService.getInterceptInfo().subscribe((intersects) => {
            if (this.sceneManagerService.getMeshes().length >= environment.MAX_MESHES) {
                this.snackBar.open(MatSnackBarMessages.MESH_LIMIT_REACHED, SnackBarConfig.Actions.OKAY, {
                    duration: SnackBarConfig.Duration.MEDIUM,
                });
                return;
            }
            this.invalidJoin = false;
            this.invalidPointGeneration = false;
            this.intersects = intersects;
            if (this.intersects[0]) {
                this.addShapeOnPlane();
            }
        });
        this.selectedShapeSub = this.componetInteractionService.getSeletedShape().subscribe((shape) => {
            this.selectedShape = shape;
            this.invalidJoin = false;
        });
        this.selectedColorSub = this.componetInteractionService.getShapeColor().subscribe((color) => {
            this.selectedColor = color;
        });
        this.route.queryParams.subscribe((params) => {
            this.isFromProductDetail = !!params["fromProductDetail"];
        });
    }

    private getTranslation(key: string, params?: object): string {
        return this.translate.instant(key, params);
    }

    private addShapeOnPlane(): void {
        if (this.interactionService.isGenerate) {
            if (!this.selectedShape && this.isShapeUnderCopy) {
                this.selectedShape = (localStorage.getItem("shape") as MeshType) || null;
            }
            const hitObject = this.intersects[0].object as Mesh;
            let point = this.intersects[0].point;

            let objectName = this.intersects[0].object.name;
            if (objectName === AuxiliaryObjectType.Plane) {
                this.locationX = point.x; // GIVES THE POINT OF INTERSECTION IN WORLD COORDS
                this.locationY = point.y;
                this.locationZ = point.z;

                // Here we make the shape sit on the grid avoiding them to go inside.
                this.locationY = this.helperService.getGroundLocationY();
            } else {
                this.locationX = point.x; // GIVES THE POINT OF INTERSECTION IN WORLD COORDS
                this.locationY = point.y;
                this.locationZ = point.z;

                if (this.faceIdentifierSrv.invalidPointOfGeneration) {
                    this.invalidPointGeneration = true;
                } else if (this.faceIdentifierSrv.state.rightFace) {
                    if (this.faceIdentifierSrv.invalidJoin) {
                        this.invalidJoin = true;
                    } else {
                        this.invalidJoin = false;
                        this.invalidPointGeneration = false;
                        this.locationZ = this.locationZ - DynamicGenerateShape.AdjustmentConstant;
                        this.locationY = hitObject.position.y;
                    }
                } else if (this.faceIdentifierSrv.state.leftFace) {
                    if (this.faceIdentifierSrv.invalidJoin) {
                        this.invalidJoin = true;
                    } else {
                        this.invalidJoin = false;
                        this.invalidPointGeneration = false;
                        this.locationZ = this.locationZ + DynamicGenerateShape.AdjustmentConstant;
                        this.locationY = hitObject.position.y;
                    }
                } else if (this.faceIdentifierSrv.state.topFace) {
                    if (this.faceIdentifierSrv.invalidJoin) {
                        this.invalidJoin = true;
                    } else {
                        this.invalidJoin = false;
                        this.invalidPointGeneration = false;
                        this.locationY = hitObject.position.y + AlteredShapeConfiguration.standardHeigthOfShape;
                    }
                } else if (this.faceIdentifierSrv.state.bottomFace) {
                    if (this.faceIdentifierSrv.invalidJoin) {
                        this.invalidJoin = true;
                    } else {
                        this.invalidJoin = false;
                        this.invalidPointGeneration = false;
                        this.locationY = hitObject.position.y - AlteredShapeConfiguration.standardHeigthOfShape;
                    }
                } else if (this.faceIdentifierSrv.state.frontFace) {
                    if (this.faceIdentifierSrv.invalidJoin) {
                        this.invalidJoin = true;
                    } else {
                        this.invalidJoin = false;
                        this.invalidPointGeneration = false;
                        this.locationX = this.locationX + DynamicGenerateShape.AdjustmentConstant;
                        this.locationY = hitObject.position.y;
                    }
                } else if (this.faceIdentifierSrv.state.curveFace) {
                    if (this.faceIdentifierSrv.invalidJoin) {
                        this.invalidJoin = true;
                    } else {
                        this.invalidJoin = false;
                        this.invalidPointGeneration = false;
                        this.locationY = hitObject.position.y;
                    }
                } else if (this.faceIdentifierSrv.state.backFace && !this.faceIdentifierSrv.state.topFace) {
                    if (this.faceIdentifierSrv.invalidJoin) {
                        this.invalidJoin = true;
                    } else {
                        this.invalidJoin = false;
                        this.invalidPointGeneration = false;
                        this.locationX = this.locationX - DynamicGenerateShape.AdjustmentConstant;
                        this.locationY = hitObject.position.y;
                    }
                } else if (this.faceIdentifierSrv.invalidJoin) {
                    this.invalidJoin = true;
                }
            }

            this.nearestGridCords = this.helperService.getNearestGridCords(new Vector2(this.locationX, this.locationZ));

            this.coOrdinates = new Vector3(this.nearestGridCords.x, this.locationY, this.nearestGridCords.y);

            const isGridNotAvailable = this.interactionService.checkIsGridNotAvailable(hitObject, this.coOrdinates);

            if (isGridNotAvailable) {
                this.snackBar.open(
                    this.getTranslation("designer.invalidMessage.gridUnavailable"),
                    SnackBarConfig.Actions.CLOSE,
                    {
                        duration: SnackBarConfig.Duration.SHORT,
                    },
                );
                return;
            }

            let selectedShapeColour = this.selectedColor ? this.selectedColor : selectedShapeColor.code;

            if (
                this.coOrdinates.z >= -CanvasConfig.maxMinConstant &&
                this.coOrdinates.z <= CanvasConfig.maxMinConstant &&
                this.coOrdinates.x >= -CanvasConfig.maxMinConstant &&
                this.coOrdinates.x <= CanvasConfig.maxMinConstant
            ) {
                if (!this.isFromProductDetail) {
                    if (this.invalidJoin) {
                        this.snackBar.open(
                            this.getTranslation("designer.invalidMessage.invalidJoin"),
                            SnackBarConfig.Actions.CLOSE,
                            {
                                duration: SnackBarConfig.Duration.SHORT,
                            },
                        );
                    } else if (this.invalidPointGeneration) {
                        this.snackBar.open(
                            this.getTranslation("designer.invalidMessage.invalidPointJoin"),
                            SnackBarConfig.Actions.CLOSE,
                            {
                                duration: SnackBarConfig.Duration.SHORT,
                            },
                        );
                    } else if (!this.invalidJoin && this.selectedShape && !this.invalidPointGeneration) {
                        // This is where generation happens
                        this.generateShape(this.coOrdinates, this.selectedShape, selectedShapeColour);
                    } else {
                        this.snackBar.open(
                            this.getTranslation("designer.invalidMessage.selectShape"),
                            SnackBarConfig.Actions.CLOSE,
                            {
                                duration: SnackBarConfig.Duration.SHORT,
                            },
                        );
                    }
                } else {
                    if (!this.isFromProductDetail) {
                        this.snackBar.open(
                            this.getTranslation("designer.invalidMessage.invalidPointGeneration"),
                            SnackBarConfig.Actions.CLOSE,
                            {
                                duration: SnackBarConfig.Duration.SHORT,
                            },
                        );
                    }
                }
            }
            this.interactionService.isGenerate = false;
        }
        localStorage.removeItem("shape");
    }

    private generateShape(coOrdinates: Vector3, shapeType: MeshType, shapeColor: Color | string | number): void {
        this.handlegenerationOfShapes(coOrdinates, shapeType, shapeColor);
        this.generatedShape.userData[Object3DUserData.initialPosition] = new Vector3();
        this.generatedShape.userData[Object3DUserData.initialPosition].copy(this.generatedShape.position);
        this.generatedShape.userData[Object3DUserData.isInCollision] = false;
        const cellReference = this.helperService.calculateRowAndColumn(this.generatedShape as Mesh);
        this.generatedShape.userData[Object3DUserData.cellReference] = cellReference;
        const rotation = this.generatedShape.rotation.clone();
        this.generatedShape.userData[Object3DUserData.rotation] = {
            x: rotation.x,
            y: rotation.y,
            z: rotation.z,
            order: rotation.order,
        };

        this.componetInteractionService.setGeneratedShapeInfo(this.generatedShape);
    }

    private handlegenerationOfShapes(coOrdinates: Vector3, shapeType: MeshType, shapeColor: any): void {
        const allmeshesInScene = this.sceneManagerService.getMeshes();
        if (this.interactionService.checkDuplicateOverlap(allmeshesInScene)) {
            return;
        }
        const objectHit = this.faceIdentifierSrv.objectHit;
        const generateAndTiltShape = (offsetX: number, offsetY: number, rotation: number) => {
            if (objectHit) {
                coOrdinates.x = objectHit.position.x + offsetX;
                coOrdinates.y = objectHit.position.y + offsetY;
            }
            this.generatedShape = this.generateNewShapes.generateNewShapes(coOrdinates, shapeType, shapeColor);
            this.generatedShape.rotateZ(rotation);
            this.generatedShape.userData[Object3DUserData.isTilted] = true;
            this.generatedShape.userData[Object3DUserData.initialPosition] = this.generatedShape.position;
            const shapeRotation = this.generatedShape.rotation.clone();
            this.generatedShape.userData[Object3DUserData.rotation] = {
                x: shapeRotation.x,
                y: shapeRotation.y,
                z: shapeRotation.z,
                order: shapeRotation.order,
            };
        };

        if (objectHit && objectHit.name === MeshType.Cube && objectHit.userData[Object3DUserData.isTilted]) {
            const offset = {
                x: 0,
                y: tiltedShapeConfiguration.defaultOffsetY,
                rotation: 0,
            };

            if (this.faceIdentifierSrv.state.rightFace) {
                generateAndTiltShape(
                    tiltedShapeConfiguration.offsetY,
                    offset.y,
                    tiltedShapeConfiguration.rotationforRightAndLeftFace,
                );
            } else if (this.faceIdentifierSrv.state.leftFace) {
                generateAndTiltShape(
                    -tiltedShapeConfiguration.offsetY,
                    offset.y,
                    -tiltedShapeConfiguration.rotationforRightAndLeftFace,
                );
            } else if (this.faceIdentifierSrv.state.topFace) {
                generateAndTiltShape(
                    objectHit.rotation.z === tiltedShapeConfiguration.rotationforRightAndLeftFace
                        ? -tiltedShapeConfiguration.offsetX
                        : tiltedShapeConfiguration.offsetX,
                    tiltedShapeConfiguration.offsetY,
                    objectHit.rotation.z,
                );
            } else if (this.faceIdentifierSrv.state.bottomFace) {
                generateAndTiltShape(
                    objectHit.rotation.z === tiltedShapeConfiguration.rotationforRightAndLeftFace
                        ? tiltedShapeConfiguration.offsetX
                        : -tiltedShapeConfiguration.offsetX,
                    -tiltedShapeConfiguration.offsetY,
                    objectHit.rotation.z,
                );
            } else if (this.faceIdentifierSrv.state.frontFace || this.faceIdentifierSrv.state.backFace) {
                generateAndTiltShape(0, 0, objectHit.rotation.z);
            }
        } else {
            this.generatedShape = this.generateNewShapes.generateNewShapes(coOrdinates, shapeType, shapeColor);
        }
    }

    ngOnDestroy(): void {
        this.interceptInfoSub.unsubscribe();
        this.selectedShapeSub.unsubscribe();
        this.selectedColorSub.unsubscribe();
    }
}
